export type GestureHandlers = {
  pan: (dx: number, dy: number) => void;
  zoom: (factor: number, x: number, y: number) => void;
  doubleTap: (x: number, y: number) => void;
};

type PointerState = { x: number; y: number };

const PAN_THRESHOLD_PX = 4;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_DISTANCE_PX = 24;
const WHEEL_ZOOM_RATE = 0.0015;
const TRACKPAD_PINCH_RATE = 0.01;

const distance = (first: PointerState, second: PointerState): number => Math.hypot(
  first.x - second.x,
  first.y - second.y,
);

const midpoint = (first: PointerState, second: PointerState): PointerState => ({
  x: (first.x + second.x) / 2,
  y: (first.y + second.y) / 2,
});

export const attachGestures = (element: HTMLElement, handlers: GestureHandlers): () => void => {
  const pointers = new Map<number, PointerState>();
  let panning = false;
  let pressStart: PointerState | null = null;
  let lastTap: { at: number; x: number; y: number } | null = null;
  let pinch: { distance: number; mid: PointerState } | null = null;

  const local = (event: PointerEvent | WheelEvent): PointerState => {
    const rect = element.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const onPointerDown = (event: PointerEvent): void => {
    element.setPointerCapture(event.pointerId);
    const point = local(event);
    pointers.set(event.pointerId, point);
    if (pointers.size === 1) {
      panning = false;
      pressStart = point;
    } else if (pointers.size === 2) {
      const [ first, second ] = [ ...pointers.values() ];
      if (first !== undefined && second !== undefined) {
        pinch = { distance: distance(first, second), mid: midpoint(first, second) };
      }
      pressStart = null;
    }
    event.preventDefault();
  };

  const onPointerMove = (event: PointerEvent): void => {
    const previous = pointers.get(event.pointerId);
    if (previous === undefined) {
      return;
    }
    const point = local(event);
    pointers.set(event.pointerId, point);
    if (pointers.size === 2 && pinch !== null) {
      const [ first, second ] = [ ...pointers.values() ];
      if (first === undefined || second === undefined) {
        return;
      }
      const nextDistance = distance(first, second);
      const nextMid = midpoint(first, second);
      if (pinch.distance > 0) {
        handlers.zoom(nextDistance / pinch.distance, nextMid.x, nextMid.y);
      }
      handlers.pan(nextMid.x - pinch.mid.x, nextMid.y - pinch.mid.y);
      pinch = { distance: nextDistance, mid: nextMid };
      return;
    }
    if (pointers.size === 1) {
      if (!panning && pressStart !== null && distance(pressStart, point) < PAN_THRESHOLD_PX) {
        return;
      }
      panning = true;
      handlers.pan(point.x - previous.x, point.y - previous.y);
    }
  };

  const onPointerUp = (event: PointerEvent): void => {
    const point = pointers.get(event.pointerId);
    pointers.delete(event.pointerId);
    if (element.hasPointerCapture(event.pointerId)) {
      element.releasePointerCapture(event.pointerId);
    }
    if (pointers.size < 2) {
      pinch = null;
    }
    if (pointers.size === 1) {
      const [ remaining ] = [ ...pointers.values() ];
      pressStart = remaining ?? null;
      panning = true;
    }
    if (pointers.size === 0 && point !== undefined && !panning && pressStart !== null) {
      const now = event.timeStamp;
      if (lastTap !== null && now - lastTap.at < DOUBLE_TAP_MS && distance(lastTap, point) < DOUBLE_TAP_DISTANCE_PX) {
        handlers.doubleTap(point.x, point.y);
        lastTap = null;
      } else {
        lastTap = { at: now, x: point.x, y: point.y };
      }
    }
    if (pointers.size === 0) {
      panning = false;
      pressStart = null;
    }
  };

  const onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    const rate = event.ctrlKey ? TRACKPAD_PINCH_RATE : WHEEL_ZOOM_RATE;
    const point = local(event);
    handlers.zoom(Math.exp(-event.deltaY * rate), point.x, point.y);
  };

  const onDoubleClick = (event: MouseEvent): void => {
    event.preventDefault();
  };

  element.addEventListener('pointerdown', onPointerDown);
  element.addEventListener('pointermove', onPointerMove);
  element.addEventListener('pointerup', onPointerUp);
  element.addEventListener('pointercancel', onPointerUp);
  element.addEventListener('wheel', onWheel, { passive: false });
  element.addEventListener('dblclick', onDoubleClick);
  return () => {
    element.removeEventListener('pointerdown', onPointerDown);
    element.removeEventListener('pointermove', onPointerMove);
    element.removeEventListener('pointerup', onPointerUp);
    element.removeEventListener('pointercancel', onPointerUp);
    element.removeEventListener('wheel', onWheel);
    element.removeEventListener('dblclick', onDoubleClick);
  };
};
